import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button2, Details, FirstItem, LastTableItem, StyledButton, TableBodyRow } from './LogisticsPage.styles.js';

import { formatDate } from '../../utils/utils.js';
import openToast from '../../utils/toast.js';

import MainContainer from '../../components/utils/MainContainer/MainContainer.js';
import TableItem from '../../components/Table/TableItem.js';
import Table from '../../components/Table/Table.js';

import { fetchDeliveries } from '../../store/slices/delivery/asyncThunks.js';
import { selectDeliveries, selectFetchDeliveriesPending } from '../../store/slices/delivery/slice.js';
import { DELIVERY_STATE, DELIVERY_TYPE } from '../../utils/constants.js';

const tableHeader = [
  {
    label: 'delivery-no',
    sortable: true,
    key: 'deliveryNo',
  },
  {
    label: 'date',
    sortable: true,
    key: 'date',
  },
  {
    label: 'status',
    sortable: true,
    key: 'status',
  },
  {
    label: 'last-update',
    sortable: true,
    key: 'lastUpdate',
  },
  {
    label: 'type',
    sortable: true,
    key: 'type',
  },
  {
    label: 'details',
    sortable: false,
    key: 'details',
  },
];

const LogisticsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toastId = useRef(null);

  const { data, maxReached, total, perPage } = useSelector(selectDeliveries);
  const loading = useSelector(selectFetchDeliveriesPending);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const pageParam = queryParams.get('page');

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchDeliveries(pageParam))
      .unwrap()
      .then(() => {})
      .catch(error => {
        openToast(toastId, 'error-toast', 'error', error.message);
      });
  }, [dispatch, pageParam]);

  const handlePageChange = pageNumber => {
    const query = { page: pageNumber };
    const queryString = new URLSearchParams(query).toString();
    const url = `/logistics?${queryString}`;

    navigate(url);
  };

  const statusLabel = delivery => {
    if (delivery.state === DELIVERY_STATE.PENDING) {
      return 'created';
    } else if (delivery.state === DELIVERY_STATE.DELIVERING) {
      return 'delivering';
    } else if (delivery.state === DELIVERY_STATE.PICKING_UP) {
      return 'pickingUp';
    } else if (delivery.state === DELIVERY_STATE.PICKED_UP) {
      if (delivery.deliveryType === DELIVERY_TYPE.PARTNER_ORDER) {
        return 'picked-up-delivering';
      } else if (delivery.deliveryType === DELIVERY_TYPE.PICK_UP) {
        return 'pickedUp';
      }
    } else if (delivery.state === DELIVERY_STATE.DELIVERED) {
      return 'delivered';
    }
    return '';
  };

  return (
    <MainContainer pageTitle={'logistics'}>
      <StyledButton
        onClick={() => {
          navigate('/create-delivery');
        }}>
        {t('create-delivery')}
      </StyledButton>
      <Button2
        onClick={() => {
          navigate('/create-delivery');
        }}>
        {t('create-delivery')}
      </Button2>
      <Table
        key={searchTerm}
        headerInformation={tableHeader}
        totalCount={total}
        currentPage={+pageParam}
        setPage={handlePageChange}
        perPage={perPage}
        noDataContent={'no-data'}
        lastColumnAligned
        maxReached={maxReached}
        dataLength={data.length}
        dataLoading={loading}
        scroll={true}
        centerText>
        {data.map((delivery, key) => {
          return (
            <TableBodyRow key={key} $offClick>
              <FirstItem showHover data={delivery?.id} />
              <TableItem data={formatDate(delivery?.createdAt) || '/'} center />
              <TableItem data={delivery?.state ? t(`${statusLabel(delivery)}`) : '/'} center />
              <TableItem data={formatDate(delivery?.updatedAt) || '/'} center />
              <TableItem data={t(delivery?.deliveryType) || '/'} center />
              <LastTableItem
                data={
                  <Details
                    onClick={() => {
                      navigate(`/delivery-details/${delivery.id}`);
                    }}
                  />
                }
                center
              />
            </TableBodyRow>
          );
        })}
      </Table>
    </MainContainer>
  );
};

export default LogisticsPage;
